import { createClient, SupabaseClient } from '@supabase/supabase-js';
import logger from './logger';

// Supabaseクライアントのシングルトンインスタンス
let supabaseClient: SupabaseClient | null = null;

/**
 * Supabaseクライアントのシングルトン取得
 * 複数のGoTrueClientインスタンス作成を防ぐため、1回のみ初期化
 */
export function getSupabaseClient(): SupabaseClient | null {
  // 既に初期化済みの場合はそれを返す
  if (supabaseClient) {
    return supabaseClient;
  }

  // 環境変数チェック（Vite環境とNode.js環境両方に対応）
  const supabaseUrl =
    typeof import.meta !== 'undefined'
      ? import.meta.env?.VITE_SUPABASE_URL
      : process.env.VITE_SUPABASE_URL;

  const supabaseAnonKey =
    typeof import.meta !== 'undefined'
      ? import.meta.env?.VITE_SUPABASE_ANON_KEY
      : process.env.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseAnonKey) {
    logger.warn('[Supabase] 環境変数が設定されていません');
    return null;
  }

  try {
    // シングルトンインスタンス作成（1回のみ）
    // ⚠️ タイムアウト設定: ネットワーク遅延対応（ローカル→Supabaseクラウド）
    supabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        flowType: 'pkce', // PKCE フローでセッション管理の安定性向上
      },
      global: {
        headers: {},
        fetch: async (url: URL | RequestInfo, options?: RequestInit) => {
          // Fetch APIのデフォルトタイムアウト設定（10秒）
          const timeoutDuration = 10000;
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), timeoutDuration);

          try {
            // 🔧 修正: Authorization ヘッダーの二重 Bearer 防止
            // Supabase がすでに正しい形式で Authorization ヘッダーを設定している場合は
            // カスタム fetch で重複設定しない
            const requestOptions = {
              ...options,
              signal: controller.signal,
            };

            const response = await fetch(url, requestOptions);
            clearTimeout(timeoutId);
            return response;
          } catch (error) {
            clearTimeout(timeoutId);
            // タイムアウト時のログ出力
            if (error instanceof Error && error.name === 'AbortError') {
              logger.warn(`[Supabase API] Request timeout after ${timeoutDuration / 1000}s`, {
                url,
              });
            }
            throw error;
          }
        },
      },
    });

    // 🔧 修正: Supabase クライアント初期化直後にセッション復元を確認
    // localStorage からセッションが正しく復元されているか確認
    supabaseClient.auth
      .getSession()
      .then(({ data: { session } }) => {
        if (session) {
          logger.debug('✅ [Supabase] セッションが localStorage から復元されました', {
            userId: session.user?.id,
            hasAccessToken: !!session.access_token,
          });
        } else {
          logger.debug('📌 [Supabase] 初期化時にセッションは存在しません（ログイン前）');
        }
      })
      .catch(err => {
        logger.warn('⚠️ [Supabase] セッション復元確認エラー:', err.message);
      });

    logger.info('✅ Supabaseクライアント（シングルトン）初期化完了');
    return supabaseClient;
  } catch (error) {
    logger.error('❌ Supabaseクライアント初期化エラー:', error);
    return null;
  }
}

/**
 * Supabaseが利用可能かチェック
 */
export function isSupabaseAvailable(): boolean {
  return getSupabaseClient() !== null;
}

/**
 * Supabase接続デバッグ情報を出力
 */
export function debugSupabaseConnection(): void {
  logger.info('🔧 Supabase接続デバッグ情報:');

  const supabaseUrl =
    typeof import.meta !== 'undefined'
      ? import.meta.env?.VITE_SUPABASE_URL
      : process.env.VITE_SUPABASE_URL;

  const supabaseAnonKey =
    typeof import.meta !== 'undefined'
      ? import.meta.env?.VITE_SUPABASE_ANON_KEY
      : process.env.VITE_SUPABASE_ANON_KEY;

  logger.info('- URL:', supabaseUrl ? '設定済み' : '未設定');
  logger.info('- Key:', supabaseAnonKey ? '設定済み' : '未設定');
  logger.info('- Client:', getSupabaseClient() ? '初期化完了' : '初期化失敗');
  logger.info('- Available:', isSupabaseAvailable());

  if (supabaseUrl) {
    logger.info('- URL (masked):', supabaseUrl.slice(0, 30) + '...');
  }
  if (supabaseAnonKey) {
    logger.info('- Key (masked):', supabaseAnonKey.slice(0, 20) + '...');
  }
}

/**
 * 認証状態とRLS権限のデバッグ情報を出力
 */
export async function debugAuthAndRLS(): Promise<void> {
  logger.info('🔧 認証状態とRLS権限デバッグ:');

  const client = getSupabaseClient();
  if (!client) {
    logger.info('❌ Supabaseクライアントが初期化されていません');
    return;
  }

  try {
    // 現在の認証状態を確認
    const {
      data: { user },
      error: authError,
    } = await client.auth.getUser();

    if (authError) {
      logger.info('❌ 認証エラー:', authError.message);
      return;
    }

    if (!user) {
      logger.info('⚠️ ユーザーが認証されていません');
      return;
    }

    logger.info('✅ 認証済みユーザー:');
    logger.info('- ユーザーID:', user.id);
    logger.info('- メールアドレス:', user.email);
    logger.info('- 認証時刻:', user.created_at);

    // RLS権限のテスト（business_partnersテーブルへの挿入権限確認）
    logger.info('🔐 RLS権限テスト中...');

    const testData = {
      name: 'テスト取引先',
      display_name: 'テスト取引先',
      type: 'corporate',
      status: 'active',
      data_source: 'custom',
      category: 'dealership',
      available_roles: ['billing', 'pickup', 'delivery'],
      default_address: 'テスト住所',
      notes: 'デバッグテスト用',
      is_system_preset: false,
    };

    // 挿入テスト（実際には実行しない - DRY RUN）
    const { error: insertError } = await client
      .from('business_partners')
      .insert([testData])
      .select()
      .limit(0); // limit(0)でDRY RUN

    if (insertError) {
      logger.info('❌ RLS挿入権限エラー:', insertError);
      logger.info('- エラーコード:', insertError.code);
      logger.info('- エラーメッセージ:', insertError.message);
      logger.info('- エラー詳細:', insertError.details);
      logger.info('- ヒント:', insertError.hint);
    } else {
      logger.info('✅ RLS挿入権限: 正常');
    }
  } catch (error) {
    logger.error('❌ デバッグ中にエラー:', error);
  }
}

/**
 * 開発用：クライアントリセット（テスト時のみ使用）
 */
export function resetSupabaseClient(): void {
  supabaseClient = null;
}
